import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, MessageSquare, TrendingUp, Eye, Calendar, Gauge, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => new Intl.NumberFormat("fr-BE").format(n || 0);

export default function AdminDashboard() {
  const [an, setAn] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics"),
      api.get("/contacts"),
    ]).then(([a, c]) => {
      setAn(a.data);
      setRecent(c.data.slice(0, 5));
    }).catch(() => {});
  }, []);

  const kpis = an ? [
    { icon: Car, label: "Véhicules", value: fmt(an.total_vehicles), sub: `${fmt(an.available)} en stock · ${fmt(an.sold)} vendus`, color: "text-[#C8A75A]" },
    { icon: Eye, label: "Vues fiches (total)", value: fmt(an.total_views), sub: "Toutes fiches cumulées", color: "text-emerald-400" },
    { icon: MessageSquare, label: "Demandes contact", value: fmt(an.contacts_total), sub: `${fmt(an.contacts_30d)} sur 30 jours`, color: "text-blue-400" },
    { icon: Gauge, label: "Bookings coaching", value: fmt(an.bookings_total), sub: `${fmt(an.bookings_confirmed)} confirmés · ${fmt(an.bookings_new)} nouveaux`, color: "text-[#C8A75A]" },
    { icon: TrendingUp, label: "Taux conversion contacts", value: `${an.conversion_contacts_pct}%`, sub: "Demandes / vues fiches", color: "text-white/80" },
    { icon: TrendingUp, label: "Taux conversion bookings", value: `${an.conversion_bookings_pct}%`, sub: "Bookings / vues fiches", color: "text-white/80" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light text-white leading-[1.05] mb-3">Dashboard</h1>
      <p className="text-white/50 text-sm mb-14 tracking-wide">Vue d'ensemble · Performances du site</p>

      {!an ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-[#1E1E1E] border border-white/10 rounded-md h-32 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {kpis.map((c, i) => (
            <div key={i} data-testid={`kpi-${i}`} className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 hover:border-[#C8A75A]/30 transition-colors">
              <c.icon className={`w-6 h-6 ${c.color} mb-6`} strokeWidth={1.3} />
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">{c.label}</p>
              <p className="font-display text-5xl text-white mt-3 leading-none">{c.value}</p>
              <p className="text-white/40 text-xs mt-3">{c.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Top 5 vehicles */}
      {an?.top_vehicles?.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-3xl text-white mb-2 leading-[1.1]">Top 5 véhicules les plus consultés</h2>
          <p className="text-white/40 text-sm mb-8">Classement selon les vues sur la fiche publique</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
            {an.top_vehicles.map((v, i) => (
              <Link key={v.id} to={`/admin/vehicles?edit=${v.id}`} data-testid={`top-vehicle-${i}`} className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden hover:border-[#C8A75A]/50 transition-colors group">
                <div className="relative aspect-[4/3] overflow-hidden bg-black">
                  <img src={v.images?.[0]} alt={v.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-[#C8A75A] text-black px-2 py-1 rounded font-semibold">#{i + 1}</span>
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{v.brand}</p>
                  <p className="font-display text-lg text-white mt-1 leading-tight line-clamp-2">{v.model}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-white/60 flex items-center gap-1"><Eye className="w-3 h-3" /> {fmt(v.views)} vues</span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-white/40 group-hover:text-[#C8A75A]" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-16">
        <h2 className="font-display text-3xl text-white mb-6 leading-[1.1]">Dernières demandes</h2>
        <div className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden">
          {recent.length === 0 ? (
            <p className="text-white/40 text-sm p-6">Aucune demande.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-white/50 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-6 py-4">Nom</th>
                  <th className="text-left px-6 py-4">Email</th>
                  <th className="text-left px-6 py-4">Type</th>
                  <th className="text-left px-6 py-4">Reçu</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-t border-white/5">
                    <td className="px-6 py-4 text-white">{c.name}</td>
                    <td className="px-6 py-4 text-white/70">{c.email}</td>
                    <td className="px-6 py-4"><span className="text-[10px] uppercase tracking-widest text-[#C8A75A]">{c.request_type}</span></td>
                    <td className="px-6 py-4 text-white/50 text-xs"><Calendar className="w-3 h-3 inline mr-1" />{new Date(c.created_at).toLocaleString("fr-BE")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
